import { useContext } from 'react';
import { Link } from 'react-router-dom';
import type { UnmetRequirement } from '@kaeliux/game-engine';
import { BUILDING_DEFINITIONS, SCIENCE_DEFINITIONS, type BuildingType, type BuildingZone, type PlayerRace, type ScienceId } from '@kaeliux/shared';
import { TeamPlanetViewContext, zoneRouteFrom } from '../contexts/TeamPlanetViewContext';
import { getBuildingIcon } from '../utils/buildingIcons';
import { getScienceIcon } from '../utils/scienceIcons';
import { Chip } from './ui/Chip';

/** Where a requirement tile leads — resolved against whichever planet the grid is shown on. */
export type RequirementTarget =
    | { type: 'building'; id: BuildingType; zone: BuildingZone; level: number }
    | { type: 'science'; id: ScienceId };

export interface RequirementTile {
    key: string;
    name: string;
    sublabel: string;
    /** Icon image, when one exists (buildings/ships) — omit to fall back to `fallbackGlyph`. */
    image?: string;
    fallbackGlyph?: string;
    /** The building/science to open on click, on the planet being viewed. */
    target?: RequirementTarget;
    /** Explicit route to navigate to on click. */
    href?: string;
    /** Same-page action instead of navigation. */
    onClick?: () => void;
}

/**
 * Converts one `getUnmetRequirements()` entry into a tile that routes
 * straight to wherever the player can act on it: the building's own zone
 * page (with it highlighted, reusing the `?highlight=` convention the
 * Research Center/Factory/Tech Center gate-redirects already use), or the Sciences
 * page (same convention) for a science. The route itself is resolved when
 * the grid renders, so the same tile lands on the Team planet's screens
 * when shown there.
 */
export function unmetRequirementTile(req: UnmetRequirement, race: PlayerRace | undefined): RequirementTile {
    if (req.type === 'building') {
        const def = BUILDING_DEFINITIONS[req.id];
        return {
            key: `building-${req.id}`,
            name: def.name,
            sublabel: `Level ${req.requiredLevel}`,
            image: getBuildingIcon(req.id, req.requiredLevel, race),
            target: { type: 'building', id: req.id, zone: def.zone, level: req.requiredLevel },
        };
    }
    const def = SCIENCE_DEFINITIONS[req.id];
    return {
        key: `science-${req.id}`,
        name: def.name,
        sublabel: `Level ${req.requiredLevel}`,
        image: getScienceIcon(req.id),
        target: { type: 'science', id: req.id },
    };
}

const personalZoneRoute = zoneRouteFrom('');

/**
 * Resolves a tile's route and sprite against the planet being viewed: on the
 * Team planet a required building links into the Team planet's own screens
 * and is drawn in that building's own Team planet architecture.
 */
function useTileResolver() {
    const teamView = useContext(TeamPlanetViewContext);
    const zoneRoute = teamView?.zoneRoute ?? personalZoneRoute;
    const sciencesRoute = teamView?.screenRoute('laboratory') ?? '/sciences';
    return {
        href: (target: RequirementTarget) => (target.type === 'building'
            ? zoneRoute(target.zone, target.id)
            : `${sciencesRoute}?highlight=${target.id}`),
        image: (tile: RequirementTile) => {
            const target = tile.target;
            const teamRace = teamView && target?.type === 'building' ? teamView.buildingRace(target.id) : undefined;
            return teamRace && target?.type === 'building' ? getBuildingIcon(target.id, target.level, teamRace) : tile.image;
        },
    };
}

const TILE_CLASSES =
    'flex flex-col items-center gap-1 rounded-tile border border-status-danger/40 bg-status-danger/10 hover:border-status-danger hover:bg-status-danger/20 transition-colors px-2 py-1.5 w-24 shrink-0';

/**
 * Renders a row of clickable requirement tiles that wraps only when it runs
 * out of horizontal room, instead of a fixed column-count grid — one tile
 * per missing building/science/mutation-donor, with an icon, name, and a
 * danger-toned `Chip` for the required level. Shared by every buildable row
 * (buildings, sciences, ships, ship upgrades) so the layout/formatting lives
 * in exactly one place. Renders null when there's nothing to show.
 */
export default function UnmetRequirementsGrid({ tiles }: { tiles: RequirementTile[] }) {
    const resolve = useTileResolver();
    if (tiles.length === 0) return null;

    return (
        <div className="border-t border-space-700 pt-2 mt-1">
            <p className="text-label text-content-muted uppercase tracking-wide mb-1.5">Requires</p>
            <div className="flex flex-wrap gap-2">
                {tiles.map((tile) => {
                    const image = resolve.image(tile);
                    const content = (
                        <>
                            {image ? (
                                <img
                                    src={image}
                                    alt=""
                                    className="w-10 h-10 rounded-control object-cover border border-space-700 bg-space-950"
                                />
                            ) : (
                                <div className="w-10 h-10 rounded-control border border-space-700 bg-space-800 flex items-center justify-center text-title">
                                    {tile.fallbackGlyph}
                                </div>
                            )}
                            <span className="text-label text-content-primary font-semibold text-center leading-tight">
                                {tile.name}
                            </span>
                            <Chip tone="danger">{tile.sublabel}</Chip>
                        </>
                    );
                    const title = `${tile.name} — ${tile.sublabel}`;
                    const href = tile.href ?? (tile.target ? resolve.href(tile.target) : undefined);
                    return href ? (
                        <Link key={tile.key} to={href} title={title} className={TILE_CLASSES}>
                            {content}
                        </Link>
                    ) : (
                        <button key={tile.key} type="button" onClick={tile.onClick} title={title} className={TILE_CLASSES}>
                            {content}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
